/**
 * Button element for a form. The btnTypes are for styling:
 *  - primary  
 *  - success
 *  - info
 *  - warning
 *  - danger
 *  - link 
 */

import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FieldType } from '@ngx-formly/core';
import { ContextService } from '../../context.service';
import { LayoutDialogComponent } from '../dialog/layout-dialog.component';
import { Subscription } from 'rxjs';


@Component({
  selector: 'mutara-button',
  template: `
      <button mat-flat-button [color]="!!props.color ? props.color : 'primary'"
        [type]="props.type ?? 'button'"
        [disabled]='props?.disabled'
        [class]="[props.className ?? 'centered']"
        (keydown.enter)="$event.preventDefault()"
        (click)="onClick($event)">
        <div>
          @if (!!props?.icon) {
            <mat-icon>{{props.icon}}</mat-icon>
          }
          {{ props.label }}
        </div>
      </button>
      @if( !!props.description ) {
        <mat-hint>{{props.description}}</mat-hint>
      }
      `,
  styles: [':host { padding-top: 0.3em; margin-left: 0.3em; margin-right: 0.3em; }', 'div { .mat-icon { padding-top: 0.25em;}}', 'button { margin-bottom: 0.3em;}'],
  standalone: false
})
export class ButtonComponent extends FieldType implements OnInit, OnDestroy {
  //TODO: mat-button -> mat-flat-button for now because defaulting to buttons that
  //look like links, should read from some config setting
  subscriptions: Subscription[] = [];

  constructor(private http: HttpClient, public host: ElementRef,
    private dialog: MatDialog, public context: ContextService) {
    super();
  }

  ngOnInit() {
    if (!!this.props.onClick && typeof this.props.onClick != 'function') {
      this.props.onClick = new Function('event', 'formState', 'context', this.props.onClick);
    }
    if (!!this.props.postScript && typeof this.props.postScript != 'function') {
      this.props.postScript = new Function('event', 'formState', 'context', this.props.postScript);
    }
    this.subscriptions.push(this.context.subscribeTo("formComplete", (validity: {valid : boolean, label? : string}) => {
      if (this.field.props?.special == 'submitForm') {
        this.field.props.disabled = !validity.valid && (this.field.props.forceEnable === undefined || !this.field.props.forceEnable);
        if (!!validity.label) {
          this.field.props.label = validity.label;
        }
      }
    }));
    try {
      if (this.field.props) { this.field.props.self = this; }
    } catch (err) {
      console.error('Setting self caused %O', err);
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
  /**
   * Execute a snipette of code that has access to the form state data
   * 
   * @param fullReference 
   * @returns 
   */
  __executeFunction(fullReference: string): any {
    let value;
    try {
      let localFunction = new Function('formState', "return " + fullReference + ";");
      value = localFunction(this.formState);
    } catch (err) {
      console.log('local function in URI error %O', err);
    }
    return value;
  }

  /**
   * Called to convert placeholders to actual values to use
   * 
   * @param paramDefn defined parameters
   * @param data 
   * @returns 
   */
  __preProcessData(paramDefn: any, data: any = this.formState) {

    if (!!paramDefn) {
      for (let [key, value] of Object.entries(paramDefn)) {
        if (typeof value != 'boolean' && value !== undefined) {
          value = value?.toString();
          // Need to handle option references (layouts) & field values (formState)
          let replaceText = (<string>value).match(/^\${(?<reference>.+)}$/);
          if (replaceText?.groups?.reference) {// Look for the function reference
            if ((replaceText.groups?.reference.indexOf('(') ?? 0) > 0) {
              value = this.__executeFunction(replaceText.groups?.reference)
            } else if (replaceText.groups.reference.indexOf('.')) { // else, try to access the data directly from the passed data
              value = data[replaceText.groups.reference];
            }
          }
        }
        paramDefn[key] = value;
      };
    }
    paramDefn['_rawData'] = data;
    return paramDefn;
  }
  /**
   * convert the urlDefn parameters into actual values for the purpose of sending the 
   * necessary data to the service engine.
   * 
   * @param urlDefn 
   */
  sendUri(urlDefn: any) {
    let paramDefn = {};

    if (!!urlDefn.params) {
      paramDefn = this.__preProcessData(urlDefn.params);
    }

    // Now post the data to the service engine
    this.subscriptions.push(this.http.post(urlDefn.service, paramDefn).subscribe({
      error: (httpError: { status: number }) => {
        if (httpError.status !== 404) {
          console.error('Error returned by service %O %O', urlDefn.service, httpError);
        }
      }
    }));
  }
  /**
   * Pop up a dialog with a set of delegated data based on a specific layout
   * 
   * @param dataDef data to set the delegate and use in the initial dialog setup
   */
  __creatDynamicDialog(dataDef: any) {
    this.context.prepareToDelegate();
    if (this.props.capForm.stopDataInterval) { this.props.capForm.stopDataInterval(); }
    this.subscriptions.push(this.http.post('$service/delegate', { delegateAs: dataDef.delegateAs }).subscribe({
      error: (err) => {
        this.context.revertDelegate();
        console.error(err);
      },
      next: (result) => {
        dataDef = Object.assign(dataDef, result);
        let popupDialog = this.dialog.open(LayoutDialogComponent,
          {
            height: '95%',
            width: '95%',
            hasBackdrop: true,
            data: dataDef,
            role: 'dialog'
          });
        this.subscriptions.push(popupDialog.beforeClosed().subscribe(() => {
          // This ends the delegated token and returns us to the proper admin state
          this.http.post('$service/clear-delegate', {}).subscribe({
            error: (err) => {
              console.error(err);
              if (this.formState.capForm.startDataInterval) { this.formState.capForm.startDataInterval(); }
            },
            next: () => {
              // Call to revert back to Admin
              this.context.revertDelegate();
              if (this.formState.capForm.startDataInterval) { this.formState.capForm.startDataInterval(); }
            }
          });
        }));
      }
    }));
  }

  /**
   * Code to launch a dialog or foward the click event to the proper handler
   * 
   * @param event 
   * @param data 
   */
  onClick(event: Event | any, data: any = undefined) {
    if (!this.props.runDialog || data === undefined) {
      if (!!this.props.onClick) {
        try {
          this.props.onClick(event, this?.formState, this.context);
        } catch (err) {
          console.error(err);
        }
      }
      if (!!this.props.urlDefn) {
        this.sendUri(this.props.urlDefn);
      }
      if (!!this.props.postScript) {
        this.props.postScript(event, this?.formState, this.context)
      }
    } else {
      let params = { ...this.props.runDialog };
      this.__creatDynamicDialog(this.__preProcessData(params, data));
    }
  }
}